/**
 * Adapts the live `EventStream` to the complexity model's {@link AccuracySource}.
 *
 * ⚠️ TODO(complexity): this is the interim implementation. Both spec documents
 * describe the gate on rest phrases and chevrons as a "WMA", but no weighted
 * moving average exists in the client — `EventStream.calcAccuracy()` is a
 * rank-weighted recency ratio over the whole accuracy queue, not a fixed-window
 * WMA. We ship on it and swap later; see TODO-1 in
 * `documentation/complexity-oracle-findings.md`.
 *
 * When the real WMA lands, add a sibling implementation of `AccuracySource` and
 * change the instance handed to `ComplexityController` — no model, policy, or
 * controller code needs to change. Note that `restAccuracyThreshold` (95) and
 * `cat` (0.80) are calibrated against THIS scale and will need re-tuning.
 */

import { AccuracySource } from './complexityModel.types';

/** The slice of `EventStream` this adapter needs. */
export interface AccuracyProvider {
  calcAccuracy(): number;
}

export class EventStreamAccuracySource implements AccuracySource {
  constructor(private readonly getStream: () => AccuracyProvider | undefined) {}

  /**
   * Live accuracy in [0, 100].
   *
   * Returns 100 when no stream is available yet: at session start there is no
   * performance history, and 100 is the value that suppresses rest-phrase
   * injection — the safe default, since injecting a rest before the user has
   * played anything would be nonsense.
   */
  currentAccuracy(): number {
    const stream = this.getStream();
    if (stream === undefined) return 100;

    const accuracy = stream.calcAccuracy();
    if (!Number.isFinite(accuracy)) return 100;
    return Math.min(Math.max(accuracy, 0), 100);
  }
}
